import { Component, Injector, afterNextRender, computed, effect, inject, input, output, signal, viewChild } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { DateAdapter, provideNativeDateAdapter } from '@angular/material/core';
import { MatDateRangePicker, MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelect, MatSelectModule } from '@angular/material/select';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DateOption, DateRange, getDateRangeFromDateOption } from '../../../../models/filters.model';

@Component({
  selector: 'fdr-past-performance-ui-date-picker',
  imports: [DatePipe, ReactiveFormsModule, MatDatepickerModule, MatFormFieldModule, MatSelectModule, TranslateModule],
  providers: [provideNativeDateAdapter()],
  templateUrl: './date-picker.component.html',
  styleUrl: './date-picker.component.scss',
  host: { style: 'display: contents' },
})
export class DatePickerComponent {
  private readonly translate = inject(TranslateService);
  private readonly injector = inject(Injector);
  private readonly dateAdapter = inject<DateAdapter<Date>>(DateAdapter);
  private readonly langChange = toSignal(this.translate.onLangChange, { initialValue: null });

  readonly initialDateOption = input<DateOption>();
  readonly initialDateRange = input<DateRange>();
  readonly resetTrigger = input<number>(0);
  readonly datesOptions = input.required<{ label: string; value: DateOption }[]>();

  readonly dateChange = output<Date[]>();
  readonly suggestionChange = output<DateOption>();
  readonly validityChange = output<boolean>();

  private readonly rangePicker = viewChild<MatDateRangePicker<Date>>('picker');

  private readonly select = viewChild(MatSelect);

  private openPickerOnClose = false;
  readonly showOptions = signal(false);

  readonly customPeriod = DateOption.customPeriod;
  readonly selectedOption = signal<DateOption | null>(null);
  readonly range = new FormGroup({
    start: new FormControl<Date | null>(null),
    end: new FormControl<Date | null>(null),
  });

  readonly localizedDatesOptions = computed(() => {
    this.langChange();
    return this.datesOptions().map(o => ({ ...o, label: this.translate.instant(o.label) }));
  });

  constructor() {
    effect(() => {
      this.langChange();
      const lang = this.translate.currentLang || this.translate.defaultLang;
      if (lang) this.dateAdapter.setLocale(lang.split('.')[0]);
    });

    effect(() => {
      this.resetTrigger();
      const option = this.initialDateOption();
      const initialRange = this.initialDateRange();
      if (!option) return;
      this.selectedOption.set(option);
      const { start, end } = initialRange ?? getDateRangeFromDateOption(option);
      this.range.setValue({ start: start.toDate(), end: end.toDate() }, { emitEvent: false });
      this.validityChange.emit(true);
    });
  }

  onOptionSelected(option: DateOption) {
    this.selectedOption.set(option);
    if (option === DateOption.customPeriod) {
      this.range.reset(undefined, { emitEvent: false });
      this.validityChange.emit(false);
      return;
    }
    const { start, end } = getDateRangeFromDateOption(option);
    this.range.setValue({ start: start.toDate(), end: end.toDate() }, { emitEvent: false });
    this.validityChange.emit(true);
    this.suggestionChange.emit(option);
  }

  onOptionClicked(option: DateOption) {
    this.openPickerOnClose = option === DateOption.customPeriod;
  }

  openOptions() {
    this.showOptions.set(true);
    setTimeout(() => this.select()?.open());
  }

  onSelectClosed() {
    this.showOptions.set(false);
    if (!this.openPickerOnClose) return;
    this.openPickerOnClose = false;
    // Wait for the range field to render so the calendar anchors below it.
    afterNextRender(() => setTimeout(() => this.rangePicker()?.open()), { injector: this.injector });
  }

  onRangeChange() {
    const { start, end } = this.range.value;
    if (!start || !end) {
      this.validityChange.emit(false);
      return;
    }
    this.selectedOption.set(DateOption.customPeriod);
    this.validityChange.emit(true);
    this.dateChange.emit([start, end]);
  }
}
